import {
    ApiBrandBenefit,
    ApiClient,
    ApiClientSettings,
    ApiContentBlock,
    ApiLandingPage,
    ApiLandingPageBannerBenefit,
    ApiLandingPageDiscount,
    ApiLandingPageIntegrations,
    ApiShopperLandingPageLink,
    ApiLandingPagePlaylist,
    ApiLPButton,
    ApiPromotion,
    LandingPageChannel,
    resolveApiLandingPage,
    ResolvedApiLandingPage,
    storefrontPromotionTakesPrecedence,
    UX_TYPE,
} from '@storefronts/common-types'

const LEGACY_PAGE_DISCOUNT_ID = -1

/**
 * Converts legacy API shape { value, type } to ApiPromotion.
 * Uses id -1 for non-ApiPromotion sources. Shared by LandingPageModel and cartTools.
 */
function legacyDiscountToApiPromotion(
    d: ApiLandingPageDiscount,
    discountCode: string | null = null,
): ApiPromotion | undefined {
    if (!discountCode?.trim() || !d.type || !d.value || d.value <= 0) {
        return
    }
    const typeLower = d.type.toLowerCase()
    return {
        id: LEGACY_PAGE_DISCOUNT_ID,
        name: '',
        bannerText: null,
        discountCode,
        discountValue: d.value,
        discountType: typeLower === 'percent' ? 'PERCENT' : 'FIXED',
        combinesWithOrderDiscounts: false,
        combinesWithProductDiscounts: d.combinesWithProductDiscounts ?? false,
        combinesWithShippingDiscounts: false,
        isOrderLevel: d.isOrderLevel ?? false,
        productScope: d.productScope,
        eligibleShopifyProductIds: d.eligibleShopifyProductIds,
    }
}

export class LandingPageModel {
    public readonly landingPageHash: string
    public readonly name: string
    public readonly title: string
    public readonly imageUrl: string
    public readonly client: ApiClient
    public readonly button: ApiLPButton | null
    public readonly defaultPlaylist?: ApiLandingPagePlaylist
    public readonly landingPageLinks: ApiShopperLandingPageLink[]
    public readonly sectionLandingPageLinks?: ApiShopperLandingPageLink[]
    public readonly cartUpsells: ApiShopperLandingPageLink[]
    public readonly bannerBenefits: ApiLandingPageBannerBenefit[]
    public readonly bodyContent: ApiContentBlock | null
    public readonly ux: UX_TYPE
    public readonly integrations: ApiLandingPageIntegrations
    public readonly channel: LandingPageChannel
    public readonly settings?: ApiClientSettings
    public readonly dynamicCartUpsellsEnabled: boolean
    public readonly ctaPulseEnabled: boolean

    private readonly _bannerText?: string | null
    private readonly _discountCode?: string | null
    private readonly _bestDiscount?: ApiPromotion | null
    private readonly _legacyDiscount?: ApiPromotion

    static from(landingPage: ApiLandingPage<ApiShopperLandingPageLink>) {
        return new LandingPageModel(resolveApiLandingPage(landingPage))
    }

    constructor(params: ResolvedApiLandingPage<ApiShopperLandingPageLink>) {
        this.landingPageHash = params.landingPageHash
        this.name = params.name
        this.title = params.title
        this.imageUrl = params.imageUrl
        this.client = params.client
        this.button = params.button
        this.defaultPlaylist = params.defaultPlaylist
        this.landingPageLinks = params.landingPageLinks
        this.sectionLandingPageLinks = params.sectionLandingPageLinks
        this.cartUpsells = params.cartUpsells
        this.bannerBenefits = params.bannerBenefits
        this.bodyContent = params.bodyContent
        this.ux = params.ux
        this.integrations = params.integrations ?? {}
        this.channel = params.channel
        this.settings = params.settings
        this.dynamicCartUpsellsEnabled =
            params.dynamicCartUpsellsEnabled ?? true
        this.ctaPulseEnabled = params.ctaPulseEnabled ?? false

        this._bannerText = params.bannerText
        this._discountCode = params.discountCode
        this._bestDiscount = params.bestDiscount ?? null
        this._legacyDiscount = params.discount
            ? legacyDiscountToApiPromotion(
                  params.discount,
                  params.discountCode ?? null,
              )
            : undefined
    }

    /**
     * STOREFRONT precedence with a page discount code: null, so the page's
     * own code, amount and banner text are used instead of the brand
     * promotion (including a scheduled window the API wrote onto it).
     * DISABLED / missing mode: null (use storefront-level or none).
     * AUTO_SYNC: bestDiscount only.
     * MANUAL / MANUAL_WITH_FALLBACK: settings.globalPromotion only.
     *
     * The landing-page API writes an active scheduled window onto
     * globalPromotion before this runs, so the returned object stays the
     * same reference for the life of the page.
     *
     * `bestDiscount` is never used as a silent fallback when no manual promotion
     * is selected — admins opt into syncing by choosing AUTO_SYNC. This keeps the
     * product tile from displaying a synced discount that the cart won't apply.
     */
    private getEffectivePromotion(): ApiPromotion | null {
        if (
            storefrontPromotionTakesPrecedence({
                promotionPrecedence: this.settings?.promotionPrecedence,
                pageDiscountCode: this._discountCode,
            })
        ) {
            return null
        }
        const mode = this.settings?.globalPromotionMode
        const manual = this.settings?.globalPromotion ?? null
        const best = this._bestDiscount ?? null

        if (mode === 'AUTO_SYNC') {
            return best
        }
        // MANUAL_WITH_FALLBACK is intentionally collapsed into MANUAL here. Its
        // correct behavior is "use manual if active, else fall back to bestDiscount",
        // but the backend returns `settings.globalPromotion` regardless of its
        // status, so we can't distinguish an active manual from an expired/deleted
        // one. Until that's addressed, both modes behave identically. See sc-33903
        // deferred follow-up.
        if (mode === 'MANUAL' || mode === 'MANUAL_WITH_FALLBACK') {
            return manual
        }
        return null
    }

    /**
     * The effective promotion when it has a code to apply at checkout or an
     * amount to show; otherwise (a banner-only window) the page's own
     * discount. An OTHER promotion has a code and a 0 amount, so it is
     * applied at checkout without changing displayed prices.
     */
    get discount(): ApiPromotion | undefined {
        const effective = this.getEffectivePromotion()
        if (
            effective &&
            (effective.discountCode?.trim() || effective.discountValue > 0)
        ) {
            return effective
        }
        return this._legacyDiscount
    }

    get bannerText(): string | undefined {
        const effective = this.getEffectivePromotion()
        if (effective) {
            return effective.bannerText || undefined
        }
        return this._bannerText || undefined
    }

    /** The code sent to checkout: always the one belonging to `discount`. */
    get discountCode(): string | undefined {
        const discount = this.discount
        if (discount) return discount.discountCode || undefined
        return this._discountCode || undefined
    }

    private getFilteredBrandBenefits(
        predicate: (benefit: ApiBrandBenefit) => boolean,
    ): ApiBrandBenefit[] {
        return (this.settings?.brandBenefits?.filter(predicate) ?? []).sort(
            (a, b) => {
                const aRank = a.rank ?? Infinity
                const bRank = b.rank ?? Infinity
                return aRank - bRank
            },
        )
    }

    get addToCartBrandBenefits(): ApiBrandBenefit[] {
        return this.getFilteredBrandBenefits(
            (benefit) => benefit.isAddToCartButtonBenefit,
        )
    }

    get buyNowBrandBenefits(): ApiBrandBenefit[] {
        return this.getFilteredBrandBenefits(
            (benefit) => benefit.isBuyNowButtonBenefit,
        )
    }

    get checkoutButtonBrandBenefits(): ApiBrandBenefit[] {
        return this.getFilteredBrandBenefits(
            (benefit) => benefit.isCheckoutButtonBenefit,
        )
    }
}
